import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, Shield, Zap, ChevronRight, RefreshCw, LogOut, Mail, XCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSTNAuth } from '@/context/STNAuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const STEPS = [
  { icon: CheckCircle, label: 'Profile Submitted',    desc: 'Your onboarding is complete',         done: true  },
  { icon: Shield,      label: 'Expert Review',        desc: 'Our team is verifying your details',  active: true },
  { icon: Zap,         label: 'Verification Done',    desc: 'Credentials confirmed',               done: false },
  { icon: ChevronRight,label: 'Full Access Granted',  desc: 'Dashboard & all features unlocked',   done: false },
];

export default function UnderReview() {
  const navigate  = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { founder, startup, logout, refreshStartup } = useSTNAuth();
  const [refreshing, setRefreshing] = useState(false);

  const isRejected = startup?.status === 'rejected';

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshStartup();
      if (startup?.status === 'verified') {
        toast.success('Your profile is verified! Welcome to StartupNexus.');
        navigate('/dashboard');
      } else {
        toast.info('Still under review — we\'ll notify you once verified.');
      }
    } catch {
      toast.error('Could not refresh status. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden px-4">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 20%, hsl(263 70% 50%), transparent)' }} />

      {/* Top controls */}
      <div className="absolute top-5 left-6 flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg gradient-primary flex items-center justify-center">
          <Zap className="w-3.5 h-3.5 text-primary-foreground" />
        </div>
        <span className="text-sm font-semibold text-foreground">StartupNexus</span>
      </div>
      <div className="absolute top-5 right-6 flex items-center gap-2">
        <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-accent text-muted-foreground">
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
        <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground gap-1.5">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg text-center"
      >
        {/* Icon */}
        <motion.div
          animate={isRejected ? {} : { rotate: [0, -8, 8, -8, 0] }}
          transition={{ duration: 1.2, delay: 0.5, repeat: Infinity, repeatDelay: 4 }}
          className={cn(
            'w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6',
            isRejected ? 'bg-destructive/10' : 'bg-primary/10'
          )}
        >
          {isRejected
            ? <XCircle className="w-10 h-10 text-destructive" />
            : <Clock className="w-10 h-10 text-primary" />
          }
        </motion.div>

        {/* Heading */}
        {isRejected ? (
          <>
            <h1 className="text-3xl font-bold text-foreground mb-3">Profile Needs Attention</h1>
            <p className="text-muted-foreground mb-2 leading-relaxed">
              Our team reviewed your profile and found some issues that need to be resolved before we can verify your startup.
            </p>
            {startup?.verificationNotes && (
              <div className="mt-3 mb-4 p-3.5 rounded-xl bg-destructive/8 border border-destructive/20 text-left">
                <p className="text-xs font-semibold text-destructive mb-1">Reviewer's Notes:</p>
                <p className="text-sm text-foreground">{startup.verificationNotes}</p>
              </div>
            )}
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-foreground mb-3">
              Your profile is <span className="gradient-text">under review</span>
            </h1>
            <p className="text-muted-foreground mb-2 leading-relaxed">
              Our experts are verifying your business credentials. You'll receive access to the full StartupNexus ecosystem once approved.
            </p>
          </>
        )}

        {/* Founder + startup info */}
        {(founder || startup) && (
          <div className="mt-4 mb-6 flex items-center justify-center gap-3 flex-wrap">
            {founder?.name && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted border border-border text-xs">
                <div className="w-5 h-5 rounded-full gradient-primary flex items-center justify-center text-primary-foreground font-bold text-[10px]">
                  {founder.name[0]?.toUpperCase()}
                </div>
                <span className="font-medium text-foreground">{founder.name}</span>
              </div>
            )}
            {startup?.companyName && (
              <div className="px-3 py-1.5 rounded-full bg-muted border border-border text-xs font-medium text-foreground">
                {startup.companyName}
              </div>
            )}
            {startup?.stnId && (
              <div className="px-3 py-1.5 rounded-full bg-muted border border-border text-xs font-mono text-muted-foreground">
                {startup.stnId}
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        {!isRejected && (
          <div className="mb-8 flex items-start justify-center gap-0">
            {STEPS.map((s, i) => (
              <div key={s.label} className="flex items-center">
                <div className="flex flex-col items-center w-24">
                  <div className={cn(
                    'w-9 h-9 rounded-full flex items-center justify-center mb-2 transition-all',
                    s.done   ? 'bg-success text-success-foreground' :
                    s.active ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' :
                               'bg-muted text-muted-foreground'
                  )}>
                    {s.active
                      ? <RefreshCw className="w-4 h-4 animate-spin" />
                      : <s.icon className="w-4 h-4" />
                    }
                  </div>
                  <p className={cn('text-[11px] font-semibold leading-tight text-center',
                    s.done ? 'text-success' : s.active ? 'text-primary' : 'text-muted-foreground'
                  )}>{s.label}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={cn('h-0.5 w-6 mb-6 flex-shrink-0',
                    STEPS[i + 1].done ? 'bg-success' : 'bg-border'
                  )} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* ETA card */}
        {!isRejected && (
          <div className="mb-6 p-4 rounded-2xl bg-card border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-warning" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-foreground">Typically verified within 24 hours</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Submitted {startup?.submittedAt
                    ? new Date(startup.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : 'recently'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {isRejected ? (
            <Button
              size="lg"
              className="gradient-primary text-primary-foreground border-0 h-11 px-8 font-semibold gap-2"
              onClick={() => navigate('/onboarding')}
            >
              Update Profile <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={handleRefresh}
              disabled={refreshing}
              className="gradient-primary text-primary-foreground border-0 h-11 px-8 font-semibold gap-2"
            >
              {refreshing
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Checking…</>
                : <><RefreshCw className="w-4 h-4" /> Check Status</>
              }
            </Button>
          )}
          <Button variant="outline" size="lg" className="h-11 px-6 gap-2" asChild>
            <a href="mailto:support@startupnexus.in">
              <Mail className="w-4 h-4" /> Contact Support
            </a>
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-6">
          Need to update your profile?{' '}
          <button onClick={() => navigate('/onboarding')} className="text-primary hover:underline">
            Edit onboarding form
          </button>
        </p>
      </motion.div>
    </div>
  );
}
